import { FC, useState } from 'react';

import classnames from 'classnames';

import { Button, HSpacing, Link, Text, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { approveBanner } from 'components/Admin/ModerationList/CampaignModeration/Banner/approveBanner';
import { BannerImages } from 'components/Admin/ModerationList/CampaignModeration/Banner/BannerImages/BannerImages';
import { BannerLastModerationStatus } from 'components/Admin/ModerationList/CampaignModeration/Banner/BannerLastModerationStatus/BannerLastModerationStatus';
import { BannerVideo } from 'components/Admin/ModerationList/CampaignModeration/Banner/BannerVideo/BannerVideo';
import { RejectBannerModal } from 'components/Admin/ModerationList/CampaignModeration/Banner/RejectBannerModal/RejectBannerModal';
import { TimeFormatter } from 'components/Admin/ModerationList/CampaignModeration/Banner/TimeFormatter/TimeFormatter';

import { AdvertisingServiceType, BannerModerationInfo } from 'store/models/moderation';
import { useDispatch } from 'store/utils';

import styles from 'components/Admin/ModerationList/CampaignModeration/Banner/Banner.module.less';

const TrlKeys = {
    advertisingServiceType: {
        [AdvertisingServiceType.Information]: 'admin.moderation.banner.advertisingServiceType.information',
        [AdvertisingServiceType.Advertising]: 'admin.moderation.banner.advertisingServiceType.advertising',
    },
    buttons: {
        approve: 'admin.moderation.banner.approve',
        reject: 'admin.moderation.banner.reject',
    },
};

interface BannerProps {
    banner: BannerModerationInfo;
    campaignId?: number;
    isModeration?: boolean;
}

export const Banner: FC<BannerProps> = ({ banner, isModeration, campaignId }) => {
    const dispatch = useDispatch();
    const trls = useTrls();

    const { isMobile } = useBreakpoint();

    const [visible, setVisibleOn, setVisibleOff] = useOnOffState(false);
    const [selectedBanner, setSelectedBanner] = useState<{
        campaignId: number;
        bannerId: number;
        advertisingServiceType: AdvertisingServiceType;
    } | null>(null);

    const {
        bannerId,
        text,
        title,
        advertisingServiceType,
        advertisementUrl,
        imageUrls,
        video,
        lastModeration,
        moderationWaitTimeInMinutes,
    } = banner;

    const buttons = (
        <>
            {isModeration && campaignId && (
                <div>
                    <VSpacing default={24} />
                    <div className={styles.buttons}>
                        <Button
                            mode="primary"
                            style="accent"
                            onClick={() => dispatch(approveBanner(bannerId, campaignId))}
                            data-qa={`moderation-banner-approve-${bannerId}`}
                        >
                            {trls(TrlKeys.buttons.approve)}
                        </Button>
                        <Button
                            mode="secondary"
                            style="accent"
                            onClick={() => {
                                setSelectedBanner({ campaignId, bannerId, advertisingServiceType });
                                setVisibleOn();
                            }}
                            data-qa={`moderation-banner-reject-${bannerId}`}
                        >
                            {trls(TrlKeys.buttons.reject)}
                        </Button>
                        <HSpacing default={0} />
                        <TimeFormatter minutes={moderationWaitTimeInMinutes} />
                    </div>
                </div>
            )}
        </>
    );

    return (
        <div>
            <div className={classnames(styles.container, styles.bannerContentWrapper)}>
                <div className={classnames(styles.container, styles.bannerDescriptionWrapper)}>
                    <div className={classnames(styles.container, styles.titleWrapper)}>
                        <Text typography="title-4-semibold" data-qa={`moderation-banner-title-${bannerId}`}>
                            {title}
                        </Text>
                        <HSpacing default={8} />
                        <Text
                            typography="label-3-regular"
                            style="secondary"
                            data-qa={`moderation-banner-id-${bannerId}`}
                        >
                            {`№${bannerId}`}
                        </Text>
                    </div>
                    <Text typography="label-3-regular" data-qa={`moderation-banner-description-${bannerId}`}>
                        {text}
                    </Text>
                    <div className={classnames(styles.container, styles.bannerAdditionalInfoWrapper)}>
                        <Text
                            typography="label-3-regular"
                            style={
                                advertisingServiceType === AdvertisingServiceType.Information ? 'secondary' : 'negative'
                            }
                            data-qa={`moderation-banner-service-type-${bannerId}`}
                        >
                            {trls(TrlKeys.advertisingServiceType[advertisingServiceType])}
                        </Text>
                        <HSpacing default={12} />
                        <Link
                            inline
                            target="_blank"
                            style="accent"
                            typography="label-3-regular"
                            href={advertisementUrl}
                            data-qa={`moderation-banner-link-${bannerId}`}
                        >
                            <Text maxLines={1}>{advertisementUrl}</Text>
                        </Link>
                    </div>
                    <BannerLastModerationStatus
                        bannerId={bannerId}
                        isModeration={isModeration}
                        lastModeration={lastModeration}
                    />
                    {!isMobile && buttons}
                </div>
                <div className={classnames(styles.container, styles.bannerMediaWrapper)}>
                    <BannerImages imagesUrls={imageUrls} bannerId={bannerId} />
                    <BannerVideo video={video} bannerId={bannerId} />
                </div>
            </div>
            {isMobile && buttons}
            <RejectBannerModal visible={visible} onClose={setVisibleOff} selectedBanner={selectedBanner} />
        </div>
    );
};
